import type { CandyCard, CandyColor, CandyEvent, CandyView } from '@campfire/game-candyland';
import { serpentine } from '@campfire/track';
import { h } from '../dom.ts';
import { name, type GameUi, type TableCtx } from '../games.ts';
import { seatColor, svg, TOKEN_SLOTS } from './track-common.ts';

export const CANDY_HEX: Record<CandyColor, string> = {
  red: '#e5484d', purple: '#8b5cf6', yellow: '#facc15', blue: '#3b82f6', orange: '#fb923c', green: '#22c55e',
};
const COLS = 14;
const ROWS = 10;
const T = 44;
const centre = (sq: number) => {
  const { row, col } = serpentine(sq, COLS, ROWS);
  return { x: col * T + T / 2, y: row * T + T / 2 };
};

function board(v: CandyView): SVGElement {
  const b = v.board;
  const parts: SVGElement[] = [];
  for (let i = 1; i <= b.length; i++) {
    const { row, col } = serpentine(i, COLS, ROWS);
    const color = b.colors[i];
    const pic = b.pictures.findIndex((p) => p.at === i);
    const castle = i === b.length;
    parts.push(
      svg('rect', {
        x: col * T + 2, y: row * T + 2, width: T - 4, height: T - 4, rx: 9,
        fill: color ? CANDY_HEX[color] : castle ? '#f4d6e6' : '#fff7e6',
        class: `cl-tile${pic >= 0 ? ' pic' : ''}${castle ? ' castle' : ''}`, 'data-square': i,
      }),
    );
    if (pic >= 0) parts.push(svg('text', { x: col * T + T / 2, y: row * T + T / 2 + 5, class: 'cl-icon' }, ['🌲', '⛰️', '🍭', '🌊'][pic] ?? '🍬'));
    if (castle) parts.push(svg('text', { x: col * T + T / 2, y: row * T + T / 2 + 6, class: 'cl-icon' }, '🏰'));
    if (b.licorice.includes(i) && v.sticky) parts.push(svg('text', { x: col * T + T / 2, y: row * T + T / 2 + 5, class: 'cl-icon' }, '⚫'));
  }
  for (const sc of b.shortcuts) {
    const a = centre(sc.from);
    const z = centre(sc.to);
    parts.push(svg('path', { d: `M${a.x} ${a.y} Q${(a.x + z.x) / 2 + 40} ${(a.y + z.y) / 2 - 40} ${z.x} ${z.y}`, class: 'cl-shortcut', 'data-shortcut': sc.name }));
  }
  // Labels for the named treat spaces.
  b.pictures.forEach((p) => {
    const c = centre(p.at);
    parts.push(svg('text', { x: Math.min(Math.max(c.x, 42), COLS * T - 42), y: c.y - T / 2 - 2 < 8 ? c.y + T / 2 + 10 : c.y - T / 2 - 2, class: 'cl-label' }, p.name));
  });
  const slotCount = new Map<number, number>();
  v.positions.forEach((pos, s) => {
    const k = slotCount.get(pos) ?? 0;
    slotCount.set(pos, k + 1);
    const slot = TOKEN_SLOTS[s % TOKEN_SLOTS.length] as readonly [number, number];
    const x = pos === 0 ? 24 + s * 26 : centre(pos).x + slot[0] * T * 0.7;
    const y = pos === 0 ? ROWS * T + 20 : centre(pos).y + slot[1] * T * 0.7;
    parts.push(
      svg('circle', { cx: x, cy: y, r: 9, fill: seatColor(s), class: `cl-token${s === v.turn ? ' turn' : ''}`, 'data-testid': `token-${s}`, 'data-pos': pos }),
      svg('text', { x, y: y + 4, class: 'cl-tok-n' }, String(s + 1)),
    );
  });
  return svg('svg', { viewBox: `0 0 ${COLS * T} ${ROWS * T + 40}`, class: 'cl-board', 'data-testid': 'candy-board', role: 'img', 'aria-label': 'Candy path board' },
    svg('text', { x: 6, y: ROWS * T + 36, class: 'cl-start' }, 'START (tap Draw to leave!)'),
    ...parts);
}

export function candyEvent(ctx: TableCtx, e: CandyEvent | null, v: CandyView): string {
  if (!e) return `${name(ctx, v.turn)} ${v.turn === ctx.you ? 'draw' : 'draws'} first. Follow the colours to the castle!`;
  const who = name(ctx, e.seat);
  const c = e.card;
  const drew = c.kind === 'picture' ? `${who} drew ${c.picture >= 0 ? v.board.pictures[c.picture]?.name : ''}` : `${who} drew ${c.double ? 'double ' : ''}${c.color}`;
  let t = drew;
  if (e.to === v.board.length) t += ' and reached the castle! 🏰';
  else if (e.shortcut) t += ` and took the ${e.shortcut}! ${e.landed}→${e.to}`;
  else t += c.kind === 'picture' ? (e.to < e.from ? ` — whoosh, back to square ${e.to}.` : ` — zoom ahead to square ${e.to}!`) : ` → square ${e.to}.`;
  if (e.stuck) t += ' Stuck in licorice: loses a turn!';
  if (e.skipped.length) t += ` ${e.skipped.map((s) => name(ctx, s)).join(' and ')} ${e.skipped.length === 1 && e.skipped[0] === ctx.you ? 'was' : 'were'} stuck and skipped.`;
  return t;
}

function cardView(v: CandyView, card: CandyCard | null): HTMLElement {
  if (!card) return h('div.cl-card.back', { 'data-testid': 'candy-card' }, h('div.cl-card-text', {}, '🍬'), h('div.muted', {}, `${v.drawCount} in the deck`));
  if (card.kind === 'picture') return h('div.cl-card.picture', { 'data-testid': 'candy-card', 'data-card': 'picture' }, h('div.cl-card-text', {}, v.board.pictures[card.picture]?.name ?? ''));
  const sq = h('div.cl-sq', { style: `background:${CANDY_HEX[card.color]}` });
  return h('div.cl-card', { 'data-testid': 'candy-card', 'data-card': `${card.double ? 'double-' : ''}${card.color}` }, card.double ? [sq.cloneNode(), sq] : sq, h('div.cl-card-text', {}, card.double ? `Double ${card.color}` : card.color));
}

export const candylandUi: GameUi = {
  id: 'candyland',
  privateHands: false,
  seatInfo: (ctx, s) => {
    const v = ctx.msg.view as CandyView;
    const p = v.positions[s] ?? 0;
    return `${p === 0 ? 'at the start' : `square ${p}`}${v.stuck[s] ? ' · stuck!' : ''}`;
  },
  render(ctx) {
    const v = ctx.msg.view as CandyView;
    const over = v.result !== null;
    const canDraw = ctx.myTurn && !over;
    return h(
      'div.candy-table',
      {},
      h('div.cl-board-wrap', {}, board(v)),
      h(
        'div.cl-side',
        {},
        cardView(v, v.last ? v.last.card : null),
        h('div.event', { 'data-testid': 'event' }, candyEvent(ctx, v.last, v)),
        h('button.primary.cl-draw', { 'data-testid': 'candy-draw', disabled: !canDraw, onclick: () => ctx.act({ type: 'draw' }) }, over ? 'Game over' : canDraw ? 'Draw a card' : `${name(ctx, v.turn)} draws…`),
        h(
          'div.ch-legend',
          {},
          v.positions.map((p, s) =>
            h(`div.ch-key${s === v.turn && !over ? '.active' : ''}`, { 'data-testid': `key-${s}` }, h('span.dot', { style: `background:${seatColor(s)}` }), `${name(ctx, s)} · ${p === 0 ? 'start' : p}${v.stuck[s] ? ' · stuck' : ''}`),
          ),
        ),
      ),
    );
  },
};
